import { useCallback, useEffect, useState, useContext } from 'react';
import { Avatar, Box, Button, Chip, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { ArrowBackIos, CheckCircle, Circle, Search, Close } from '@mui/icons-material';
import { Formik, Form } from 'formik';

import { CARD_FORM_STEP } from '../../CreateCard';
import { CARD_TYPE, SMALL_DESKTOP, KYC_STATUS } from 'helpers/constants';
import { CardContext } from 'context/cards/cardContext';
import { HIDE_CARD_FORM_MODAL, OPEN_USER_FORM_MODAL } from 'context/cards/types';
import { Users } from 'lib/api';
import physicalCard from 'assets/createCards/physicalCard.png';
import useDebounce from 'Hooks/useDebounce';
import virtualCard from 'assets/createCards/virtualCard.png';
import BlueToolTip from 'components/shared/BlueTooltip';

const SelectCardHolder = ({ card, setCardholder, onBackBtnClick, onStepSubmit, onCardModalClose }) => {
    const { dispatch, shouldUpdateCardholders } = useContext(CardContext);
    const [cardholders, setCardholders] = useState([]);
    const [cardholderName, setCardholderName] = useState('');
    const debouncedCardholderName = useDebounce(cardholderName, 500);
    const [isCardholderSelected, setIsCardholderSelected] = useState(false);
    const [isCardholderIncomplete, setIsCardholderIncomplete] = useState(false);

    useEffect(() => {
        if (card?.cardholderId) {
            setIsCardholderSelected(true);
        }
    }, [card?.cardholderId]);

    const fetchUsers = useCallback(async () => {
        try {
            const queryParams = {
                ...(debouncedCardholderName && { search: debouncedCardholderName }),
                limit: 100,
                order: 'DESC'
            };
            const { results } = await Users.getUsers(queryParams);
            setCardholders(results);
        } catch (error) {
            console.error(error);
        }
    }, [debouncedCardholderName]);
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, shouldUpdateCardholders]);

    useEffect(() => {
        if (shouldUpdateCardholders) fetchUsers();
    }, [fetchUsers, shouldUpdateCardholders]);

    const getUserNameInitials = user => {
        return `${user.first_name[0]}${user.last_name[0]}`;
    };

    const getAvatarColor = user => {
        const userIndex = cardholders.indexOf(user) % Object.keys(avatarBackgroundColors).length;
        const color = Object.values(avatarBackgroundColors)[userIndex];
        return color;
    };

    const handleSearchChange = ({ target: input }) => {
        setCardholderName(input.value);
    };

    const handleCardholderClick = (cardholder, setFieldValue) => {
        cardholder.person_id ? setIsCardholderIncomplete(false) : setIsCardholderIncomplete(true);
        cardholder.is_incomplete = cardholder.person_id ? false : true;
        setFieldValue('cardholderId', cardholder.id);
        setCardholder(cardholder);
        setIsCardholderSelected(true);
    };

    const handleAddCardholderClick = () => {
        dispatch({ type: OPEN_USER_FORM_MODAL });
        dispatch({ type: HIDE_CARD_FORM_MODAL });
    };

    const avatarBackgroundColors = {
        Mint: '#6CCDB433',
        lightGreen: '#DAEDCA99',
        lightYellow: '#FFE1BD99',
        Sky20: '#1BABE233',
        Tangerine: '#EE8A684D'
    };

    return (
        <Box className="create-card" sx={{ width: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Box className="create-card__modal-title">Select the cardholder</Box>
                    <Box className="create-card__modal-paginator">
                        <span className="create-card__modal-page-indicator">2/4 Steps</span>
                        <Circle className="create-card__indicator-active" />
                        <Circle className="create-card__indicator-active" />
                        <Circle className="create-card__indicator-inactive" />
                        <Circle className="create-card__indicator-inactive" />
                    </Box>
                </Box>

                <Box alignSelf="start">
                    <IconButton onClick={onCardModalClose}>
                        <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                    </IconButton>
                </Box>
            </Box>
            <Grid container columnSpacing={{ md: 3 }}>
                <Grid item md={6}>
                    <Box display="flex" justifyContent="space-between" sx={{ marginBottom: '12px' }}>
                        <TextField
                            inputProps={{
                                style: {
                                    padding: '10px'
                                }
                            }}
                            name="cardholder"
                            variant="outlined"
                            placeholder="Search cardholder"
                            size="small"
                            value={cardholderName}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button variant="text" sx={{ fontSize: '13px' }} onClick={handleAddCardholderClick}>
                            + {SMALL_DESKTOP ? 'New' : 'Add New Cardholder'}
                        </Button>
                    </Box>
                    <Formik initialValues={card} enableReinitialize={true} onSubmit={onStepSubmit}>
                        {({ setFieldValue, values }) => (
                            <Form id="card-cardholder-form">
                                <Box className="create-card__cardholder-list">
                                    {cardholders.map(cardholder => (
                                        <Box
                                            key={cardholder.id}
                                            className={`
                                                    create-card__cardholder-item${' '}
                                                    ${
                                                        cardholder.id === values.cardholderId &&
                                                        'create-card__cardholder-item-selected'
                                                    }
                                                `}
                                            onClick={() => {
                                                if (cardholder.kyc_status === KYC_STATUS.APPROVED) {
                                                    handleCardholderClick(cardholder, setFieldValue);
                                                }
                                            }}
                                            sx={{
                                                backgroundColor: cardholder.kyc_status !== KYC_STATUS.APPROVED && '#F6F7F7'
                                            }}>
                                            <Box display="flex" justifyContent="space-between">
                                                <Box display="flex">
                                                    <Avatar
                                                        variant="square"
                                                        className="create-card__cardholder-avatar"
                                                        sx={{ bgcolor: getAvatarColor(cardholder) || '#bdbdbd' }}>
                                                        {getUserNameInitials(cardholder)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography component="div" className="create-card__cardholder-name">
                                                            {`${cardholder.first_name} ${cardholder.last_name}`}
                                                        </Typography>
                                                        {cardholder.id !== values.cardholderId && (
                                                            <Typography
                                                                component="div"
                                                                className="create-card__cardholder-category">
                                                                '
                                                                {cardholder?.job_title && cardholder?.department
                                                                    ? `${cardholder?.job_title} | ${cardholder.department}`
                                                                    : 'N/A'}
                                                                '
                                                            </Typography>
                                                        )}
                                                        {!isCardholderIncomplete && cardholder.id === values.cardholderId && (
                                                            <Typography
                                                                component="div"
                                                                className="create-card__cardholder-category">
                                                                '
                                                                {cardholder?.job_title && cardholder?.department
                                                                    ? `${cardholder?.job_title} | ${cardholder.department}`
                                                                    : 'N/A'}
                                                                '
                                                            </Typography>
                                                        )}
                                                        {isCardholderIncomplete && cardholder.id === values.cardholderId && (
                                                            <>
                                                                <Typography
                                                                    component="div"
                                                                    className="create-card__cardholder-category">
                                                                    Please complete the profile to create a card
                                                                </Typography>
                                                                <Box>
                                                                    <Button variant="outlined" type="submit">
                                                                        Complete Profile
                                                                    </Button>
                                                                </Box>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Box>
                                                {cardholder.kyc_status !== KYC_STATUS.APPROVED && (
                                                    <Box>
                                                        <BlueToolTip
                                                            placement="top"
                                                            arrow
                                                            title={
                                                                <Typography className="tooltip-stat-text white-text">
                                                                    You can only create card of KYC approved users
                                                                </Typography>
                                                            }>
                                                            <Chip label="KYC PENDING" className="create-card__kyc-pending" />
                                                        </BlueToolTip>
                                                    </Box>
                                                )}
                                                {cardholder.id === values.cardholderId && (
                                                    <CheckCircle sx={{ color: 'var(--info-color)', fontSize: '19px' }} />
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Grid>
                <Grid item md={6}>
                    <Box className="create-card__card-banner-container">
                        <Box className="create-card__card-image-container">
                            <img
                                className="create-card__card-image"
                                src={card.type === CARD_TYPE.VIRTUAL ? virtualCard : physicalCard}
                                alt="virtual card"
                            />
                        </Box>
                        <Box className="create-card__card-banner-benefits">
                            <Box width="300px">
                                <Box display="flex" alignItems="center" marginBottom="22px">
                                    <CheckCircle sx={{ color: '#1BABE2', marginRight: '14px' }} />
                                    <Typography component="span" className="create-card__card-banner-benefit">
                                        Works anywhere Visa is accepted
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" marginBottom="22px">
                                    <CheckCircle sx={{ color: '#1BABE2', marginRight: '14px' }} />
                                    <Typography component="span" className="create-card__card-banner-benefit">
                                        Earns Bitcoin with every transaction
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <CheckCircle sx={{ color: '#1BABE2', marginRight: '14px' }} />
                                    <Typography component="span" className="create-card__card-banner-benefit">
                                        Incurs zero transaction fees on Paystand Network
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Box display="flex" justifyContent="space-between" sx={{ marginTop: '20px' }}>
                <Button variant="outlined" onClick={() => onBackBtnClick(CARD_FORM_STEP.TYPE)}>
                    <ArrowBackIos fontSize="small" />
                </Button>
                <Button variant="contained" type="submit" form="card-cardholder-form" disabled={!isCardholderSelected}>
                    Continue
                </Button>
            </Box>
        </Box>
    );
};

export default SelectCardHolder;
